import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { ProposalStatus } from "./types";

/** Central query-key factory so live-update invalidation stays consistent. */
export const qk = {
  me: ["me"] as const,
  users: ["users"] as const,
  projects: ["projects"] as const,
  project: (pid: string) => ["project", pid] as const,
  workflow: (pid: string) => ["project", pid, "workflow"] as const,
  workflowVersions: (pid: string) => ["project", pid, "workflow-versions"] as const,
  context: (pid: string) => ["project", pid, "context"] as const,
  contextVersions: (pid: string) => ["project", pid, "context-versions"] as const,
  envTemplates: (pid: string) => ["project", pid, "env-templates"] as const,
  contextSources: (pid: string) => ["project", pid, "context-sources"] as const,
  secrets: (pid: string) => ["project", pid, "secrets"] as const,
  usage: (pid: string) => ["project", pid, "usage"] as const,
  tasks: (pid: string) => ["project", pid, "tasks"] as const,
  proposals: (pid: string, status?: ProposalStatus) =>
    status ? (["project", pid, "proposals", status] as const) : (["project", pid, "proposals"] as const),
  task: (tid: string) => ["task", tid] as const,
  timeline: (tid: string) => ["task", tid, "timeline"] as const,
  diff: (tid: string) => ["task", tid, "diff"] as const,
  environment: (tid: string) => ["task", tid, "environment"] as const,
  run: (rid: string) => ["run", rid] as const,
  runEvents: (rid: string) => ["run", rid, "events"] as const,
  revisions: (eid: string) => ["revisions", eid] as const,
  agentPool: ["agent-pool"] as const,
  monitorEnvs: (recent: boolean) => ["monitor", "environments", recent] as const,
  monitorEnvLogs: (id: string) => ["monitor", "env-logs", id] as const,
  monitorRuns: ["monitor", "runs"] as const,
  notifications: ["notifications"] as const,
  unreadCount: ["notifications", "unread-count"] as const,
};

export const useProjects = () => useQuery({ queryKey: qk.projects, queryFn: api.projects });
export const useProject = (pid: string) =>
  useQuery({ queryKey: qk.project(pid), queryFn: () => api.project(pid), enabled: !!pid });
export const useWorkflow = (pid: string) =>
  useQuery({ queryKey: qk.workflow(pid), queryFn: () => api.workflow(pid), enabled: !!pid });
export const useTasks = (pid: string) =>
  useQuery({ queryKey: qk.tasks(pid), queryFn: () => api.tasks(pid), enabled: !!pid });
export const useEnvTemplates = (pid: string) =>
  useQuery({ queryKey: qk.envTemplates(pid), queryFn: () => api.envTemplates(pid), enabled: !!pid });
export const useContextSources = (pid: string) =>
  useQuery({
    queryKey: qk.contextSources(pid),
    queryFn: () => api.contextSources(pid),
    enabled: !!pid,
  });
export const useTask = (tid: string) =>
  useQuery({ queryKey: qk.task(tid), queryFn: () => api.task(tid), enabled: !!tid });
export const useTimeline = (tid: string) =>
  useQuery({ queryKey: qk.timeline(tid), queryFn: () => api.timeline(tid), enabled: !!tid });
export const useProposals = (pid: string, status: ProposalStatus) =>
  useQuery({
    queryKey: qk.proposals(pid, status),
    queryFn: () => api.proposals(pid, status),
    enabled: !!pid,
  });
export const useUnreadCount = () =>
  useQuery({ queryKey: qk.unreadCount, queryFn: api.unreadCount, refetchInterval: 60_000 });
